import React, { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { BookOpen, Zap, Layers, ClipboardCheck, Calculator, Shapes, NotebookPen, RotateCcw, Puzzle } from "lucide-react";
import { COURSES } from "@/data";
import { useLocalStorage } from "@/lib/storage";
import { printDocument, cancelPrint } from "@/lib/pdf";
import Navbar from "@/components/app/Navbar";
import NotesView from "@/components/app/NotesView";
import SummaryView from "@/components/app/SummaryView";
import Flashcards from "@/components/app/Flashcards";
import ExamView, { examScore } from "@/components/app/ExamView";
import HardnessCalculator from "@/components/app/HardnessCalculator";
import SkillQuiz from "@/components/app/SkillQuiz";
import SkillMatch from "@/components/app/SkillMatch";
import MistakeBook, { mistakeEntries } from "@/components/app/MistakeBook";
import PrintDoc from "@/components/app/PrintDoc";

const ascii = (s) => s.replace(/İ/g, "I").replace(/Ö/g, "O").replace(/Ü/g, "U").replace(/Ş/g, "S").replace(/Ç/g, "C").replace(/Ğ/g, "G");

function App() {
  const prefersDark = typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  const [theme, setTheme] = useLocalStorage("theme", prefersDark ? "dark" : "light");
  const [courseKey, setCourseKey] = useLocalStorage("course", "k453");
  const [module, setModule] = useLocalStorage("module", "notes");
  const [unitIds, setUnitIds] = useLocalStorage("unit", {});
  const [examIds, setExamIds] = useLocalStorage("examSel", {});
  const [read, setRead] = useLocalStorage("read", {});
  const [cardStatus, setCardStatus] = useLocalStorage("cards", {});
  const [answers, setAnswers] = useLocalStorage("answers", {});
  const [mistakes, setMistakes] = useLocalStorage("mistakes", {});
  const [skillStats, setSkillStats] = useLocalStorage("skillquiz", { total: 0, correct: 0, best: 0 });
  const [matchStats, setMatchStats] = useLocalStorage("skillmatch", { games: 0, best: 0, bestTime: 0, perfect: 0, lastScore: 0 });
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(max-width: 760px)").matches);
  const [mbScope, setMbScope] = useState(courseKey);
  const [job, setJob] = useState(null);
  const printRef = useRef(null);
  const runningRef = useRef(null);

  const course = COURSES[courseKey] || COURSES.k453;
  const examId = examIds[course.key] || null;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => setMbScope(course.key), [course.key]);

  // Mobil algılama (toast konumu ve sekme davranışı için)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Aktif modül sekmesini görünür alana kaydır (mobilde yatay kaydırmalı sekmeler)
  useEffect(() => {
    const el = document.querySelector(".mtab.active");
    el?.scrollIntoView?.({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [module, course.key]);

  useEffect(() => {
    if (!job || runningRef.current === job) return;
    runningRef.current = job;
    (async () => {
      await new Promise((r) => setTimeout(r, 250));
      try {
        await printDocument(job.filename);
        toast.success("PDF penceresi kapatıldı", { description: "Kaydetmediysen tekrar 'PDF İndir' düğmesine basabilirsin." });
      } catch (e) {
        console.error(e);
        toast.error("PDF oluşturulamadı. Lütfen tekrar deneyin.");
      } finally {
        runningRef.current = null;
        setJob(null);
      }
    })();
  }, [job]);

  const pdfLabel =
    module === "exams" && examId ? "Sınav + Çözümler karnesi" : module === "mistakes" ? "Hata Defteri" : "Ders Notları + Hap Bilgiler";

  const startPdf = (forceKind) => {
    if (job) return;
    const kind = forceKind || (module === "exams" && examId ? "exam" : module === "mistakes" ? "mistakes" : "notes");
    const code = ascii(course.code);
    if (kind === "exam") {
      const exam = course.exams.find((e) => e.id === examId);
      if (!exam) return;
      setJob({ kind, course, exam, ans: answers[exam.id] || {}, filename: `${code}_${ascii(exam.title).replace(/\s+/g, "_")}_Karne.pdf` });
    } else if (kind === "mistakes") {
      const list = mistakeEntries(mistakes, mbScope);
      if (!list.length) return toast("Hata defterin boş", { description: "İndirilecek yanlış soru bulunmuyor." });
      setJob({ kind, course, list, filename: `Hata_Defterim_${mbScope === "all" ? "Tum_Dersler" : code}.pdf` });
    } else {
      setJob({ kind: "notes", course, filename: `${code}_Ders_Notlari_ve_Hap_Bilgiler.pdf` });
    }
    toast("PDF hazırlanıyor…", { description: "Açılan pencerede hedef olarak “PDF olarak kaydet” seç. Sayfalar A4'e göre otomatik bölünür." });
  };

  const stats = useMemo(() => {
    const readN = course.notes.filter((u) => read[`${course.key}-${u.id}`]).length;
    const know = course.cards.filter((_, i) => cardStatus[`${course.key}-${i}`] === "know").length;
    const exams = course.exams.map((e) => examScore(e, answers[e.id]));
    const mist = Object.values(mistakes).filter((m) => m.course === course.key).length;
    return { readN, know, exams, mist };
  }, [course, read, cardStatus, answers, mistakes]);

  const MODULES = [
    { key: "notes", label: "Ders Notları", icon: BookOpen },
    { key: "summary", label: "Hap Bilgiler", icon: Zap },
    { key: "cards", label: "Bilgi Kartları", icon: Layers },
    { key: "exams", label: "Deneme Sınavları", icon: ClipboardCheck },
    { key: "tool", label: course.tool.label, icon: course.key === "k453" ? Calculator : Shapes },
    ...(course.key === "k405" ? [{ key: "match", label: "Beceri Eşleştirme", icon: Puzzle }] : []),
    { key: "mistakes", label: "Hata Defterim", icon: NotebookPen, count: stats.mist },
  ];

  // Ders değişince mevcut derste olmayan bir modül seçiliyse ders notlarına dön
  useEffect(() => {
    if (!MODULES.some((m) => m.key === module)) setModule("notes");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.key, module]);

  const resetAll = () => {
    if (!window.confirm("Tüm ilerleme (okunan üniteler, kartlar, sınav cevapları, hata defteri) silinsin mi?")) return;
    setRead({}); setCardStatus({}); setAnswers({}); setMistakes({}); setSkillStats({ total: 0, correct: 0, best: 0 });
    setMatchStats({ games: 0, best: 0, bestTime: 0, perfect: 0, lastScore: 0 });
    toast.success("İlerleme sıfırlandı");
  };

  return (
    <div className="app" data-course={course.key}>
      <Toaster position={isMobile ? "top-center" : "bottom-right"} theme={theme} richColors visibleToasts={3} closeButton />
      <Navbar
        course={course.key}
        setCourse={setCourseKey}
        theme={theme}
        toggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
        onPdf={() => startPdf()}
        pdfBusy={!!job}
        pdfLabel={pdfLabel}
      />

      <div className="course-band">
        <div className="wrap band-inner">
          <div className="band-title">
            <span className="band-code">{course.code}</span>
            <h2>{course.name}</h2>
          </div>
          <div className="band-stats" data-testid="course-stats">
            <div><b>{stats.readN}/{course.notes.length}</b><span>ünite okundu</span></div>
            <div><b>{stats.know}/{course.cards.length}</b><span>kart biliniyor</span></div>
            {stats.exams.map((s, i) => (
              <div key={i}><b>{s.correct}/{s.total}</b><span>Deneme {i + 1}</span></div>
            ))}
            <div><b className={stats.mist ? "bad" : ""}>{stats.mist}</b><span>hata</span></div>
          </div>
        </div>
        <nav className="wrap module-tabs" data-testid="module-tabs">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <button key={m.key} className={`mtab ${module === m.key ? "active" : ""}`} onClick={() => { setModule(m.key); window.scrollTo({ top: 0 }); }} data-testid={`module-tab-${m.key}`}>
                <Icon size={16} />
                <span>{m.label}</span>
                {m.count > 0 && <span className="mtab-count">{m.count}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      <main className="wrap main" key={course.key + module}>
        {module === "notes" && (
          <NotesView course={course} read={read} setRead={setRead} unitId={unitIds[course.key]} setUnitId={(id) => setUnitIds({ ...unitIds, [course.key]: id })} />
        )}
        {module === "summary" && (
          <>
            <div className="inline-pdf">
              <button className="btn btn-accent" onClick={() => startPdf("notes")} disabled={!!job} data-testid="summary-pdf-btn">Ders Notları + Hap Bilgiler PDF</button>
            </div>
            <SummaryView course={course} />
          </>
        )}
        {module === "cards" && <Flashcards course={course} status={cardStatus} setStatus={setCardStatus} />}
        {module === "exams" && (
          <ExamView
            course={course}
            answers={answers}
            setAnswers={setAnswers}
            mistakes={mistakes}
            setMistakes={setMistakes}
            examId={examId}
            setExamId={(id) => setExamIds({ ...examIds, [course.key]: id })}
            onPdf={() => startPdf("exam")}
            pdfBusy={!!job}
            goMistakes={() => { setModule("mistakes"); window.scrollTo({ top: 0 }); }}
          />
        )}
        {module === "tool" && (course.key === "k453" ? <HardnessCalculator /> : <SkillQuiz stats={skillStats} setStats={setSkillStats} />)}
        {module === "match" && course.key === "k405" && <SkillMatch stats={matchStats} setStats={setMatchStats} />}
        {module === "mistakes" && (
          <MistakeBook course={course} mistakes={mistakes} setMistakes={setMistakes} scope={mbScope} setScope={setMbScope} onPdf={() => startPdf("mistakes")} pdfBusy={!!job} />
        )}
      </main>

      <footer className="wrap footer">
        <span>Tüm ilerlemen yalnızca bu tarayıcıda saklanır · İnternet bağlantısı olmadan da çalışır.</span>
        <button className="btn btn-ghost" onClick={resetAll} data-testid="reset-all-btn"><RotateCcw size={14} /> Tüm ilerlemeyi sıfırla</button>
      </footer>

      {job && (
        <>
          <div className="pdf-overlay" data-testid="pdf-overlay">
            <div className="pdf-overlay-card">
              <span className="loader" />
              <div className="pdf-overlay-text">
                <b>PDF penceresi açılıyor…</b>
                <span>Hedef olarak “PDF olarak kaydet” seç. Pencere açılmadıysa tarayıcı açılır pencerelere izin ver.</span>
              </div>
              <button className="btn btn-ghost" onClick={cancelPrint} data-testid="pdf-cancel-btn">Kapat</button>
            </div>
          </div>
          <div className="print-view" ref={printRef} data-testid="print-view"><PrintDoc job={job} /></div>
        </>
      )}
    </div>
  );
}

export default App;
